/**
 * Product Card Media Slideshow
 * Efficiently handles image slideshows for product cards with lazy initialization
 */
export class ProductCardMedia extends HTMLElement {
  swiper = null
  observer = null

  connectedCallback() {
    // Only initialize if there are multiple images
    const slides = this.querySelectorAll('.swiper-slide')
    if (slides.length <= 1) {
      // Single image - no slideshow needed
      return
    }

    const container = this.querySelector('.product-card-media-swiper')
    if (!container) return

    // Setup hover handlers for arrow visibility
    this.setupArrowHover()

    // Check if already visible (for elements above fold)
    const checkVisibility = () => {
      const rect = this.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight + 50 && rect.bottom > -50

      if (isVisible && !this.swiper) {
        // Already visible, initialize immediately
        this.initSwiper()
        if (this.observer) {
          this.observer.unobserve(this)
        }
      }
    }

    // Check immediately
    checkVisibility()

    // Also use Intersection Observer for elements that become visible later
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.swiper) {
            this.initSwiper()
            this.observer.unobserve(this)
          }
        })
      },
      {
        rootMargin: '50px', // Start loading slightly before visible
        threshold: 0.01,
      }
    )

    this.observer.observe(this)
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.swiper) {
      this.swiper.destroy(true, true)
    }
    this.stopAutoSlide()
  }

  initSwiper() {
    const container = this.querySelector('.product-card-media-swiper')
    if (!container) return

    // Swiper is now loaded directly, no need for async loading
    if (!window.Swiper || !window.SwiperNavigation) {
      console.warn('Swiper not available')
      return
    }

    const Swiper = window.Swiper
    const SwiperNavigation = window.SwiperNavigation

    // Wait a frame to ensure DOM is updated
    requestAnimationFrame(() => {
      const prevButton = container.querySelector('.product-card-media-nav-prev')
      const nextButton = container.querySelector('.product-card-media-nav-next')

      // Get Navigation module if available
      const swiperModules = []
      if (SwiperNavigation) {
        swiperModules.push(SwiperNavigation)
      }

      // Minimal configuration for performance
      this.swiper = new Swiper(container, {
        modules: swiperModules,
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        allowTouchMove: true,
        touchEventsTarget: 'container',
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation:
          prevButton && nextButton
            ? {
                nextEl: nextButton,
                prevEl: prevButton,
              }
            : false,
        on: {
          init: () => {
            // Remove x-cloak from all slides after Swiper is initialized
            const slides = this.querySelectorAll('.swiper-slide[x-cloak]')
            slides.forEach((slide) => {
              slide.removeAttribute('x-cloak')
            })
          },
        },
      })

      container.swiper = this.swiper

      // Add hover navigation for desktop
      if (window.innerWidth >= 768) {
        this.setupHoverNavigation()
      }
    })
  }

  setupArrowHover() {
    const prevButton = this.querySelector('.product-card-media-nav-prev')
    const nextButton = this.querySelector('.product-card-media-nav-next')

    if (!prevButton || !nextButton) return

    // Add md:!hidden class by default (hidden on desktop)
    prevButton.classList.add('md:!hidden')
    nextButton.classList.add('md:!hidden')

    // Show arrows on mouse enter
    this.addEventListener('mouseenter', () => {
      prevButton.classList.remove('md:!hidden')
      nextButton.classList.remove('md:!hidden')
    })

    // Hide arrows on mouse leave
    this.addEventListener('mouseleave', () => {
      prevButton.classList.add('md:!hidden')
      nextButton.classList.add('md:!hidden')
    })
  }

  setupHoverNavigation() {
    let hoverTimeout = null
    const slides = this.querySelectorAll('.swiper-slide')
    if (slides.length <= 1) return

    this.addEventListener('mouseenter', () => {
      if (hoverTimeout) clearTimeout(hoverTimeout)
      hoverTimeout = setTimeout(() => {
        this.startAutoSlide()
      }, 500) // Delay before starting auto-slide
    })

    this.addEventListener('mouseleave', () => {
      if (hoverTimeout) clearTimeout(hoverTimeout)
      this.stopAutoSlide()
    })
  }

  startAutoSlide() {
    if (!this.swiper || this.swiper.slides.length <= 1) return

    const autoSlide = () => {
      if (this.swiper && document.contains(this)) {
        this.swiper.slideNext()
        this.autoSlideTimeout = setTimeout(autoSlide, 3000)
      }
    }

    this.stopAutoSlide() // Clear any existing timeout
    this.autoSlideTimeout = setTimeout(autoSlide, 3000)
  }

  stopAutoSlide() {
    if (this.autoSlideTimeout) {
      clearTimeout(this.autoSlideTimeout)
      this.autoSlideTimeout = null
    }
  }
}
